import { computed, onBeforeUnmount, type ComputedRef, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { ScrollManager } from './ScrollManager'

const DEFAULT_BOUNDARY_SELECTOR = '[data-menu-boundary]'
const DEFAULT_PADDING_PX = 8
const MIN_USEFUL_HEIGHT_PX = 96
export const MENU_MAX_HEIGHT_CLASS = 'ds-menu-max-height'

export type UseMenuMaxHeight = {
  subscribe: () => void
  unsubscribe: () => void
  popupContentClass: ComputedRef<string | undefined>
  popupContentStyle: ComputedRef<Record<string, string> | undefined>
}

type Options = {
  padding?: number
  boundarySelector?: string
}

type Bounds = {
  topBound: number
  bottomBound: number
  boundaries: Element[]
}

// Exported for unit testing. Looks for boundary markers within the innermost ancestor scope
// of `anchorEl` (resolved via `:has()`). Boundaries below the anchor cap downward space,
// above cap upward. Falls back to viewport edges if no scope contains a boundary.
export const findBounds = (anchorEl: HTMLElement, selector: string): Bounds => {
  const anchorRect = anchorEl.getBoundingClientRect()
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  const fallback: Bounds = { topBound: 0, bottomBound: viewportHeight, boundaries: [] }

  const scope = anchorEl.closest(`:has(${selector})`)

  if (scope === null) {
    return fallback
  }

  const candidates = Array.from(scope.querySelectorAll(selector))
  if (candidates.length === 0) {
    return fallback
  }

  let topBound = 0
  let bottomBound = viewportHeight

  for (const candidate of candidates) {
    const candidateRect = candidate.getBoundingClientRect()
    if (candidateRect.top >= anchorRect.bottom) {
      bottomBound = Math.min(bottomBound, candidateRect.top)
    } else if (candidateRect.bottom <= anchorRect.top) {
      topBound = Math.max(topBound, candidateRect.bottom)
    }
  }

  return { topBound, bottomBound, boundaries: candidates }
}

export const useMenuMaxHeight = (
  getAnchorEl: () => HTMLElement | undefined,
  options: Options = {},
): UseMenuMaxHeight => {
  const padding = options.padding ?? DEFAULT_PADDING_PX
  const selector = options.boundarySelector ?? DEFAULT_BOUNDARY_SELECTOR

  const maxHeight = ref<number | null>(null)

  let resizeObserver: ResizeObserver | null = null
  let observedBoundaries: Element[] = []

  const compute = (): void => {
    const el = getAnchorEl()

    if (!el) {
      maxHeight.value = null
      return
    }

    const anchorRect = el.getBoundingClientRect()
    const { topBound, bottomBound } = findBounds(el, selector)

    const spaceBelow = bottomBound - anchorRect.bottom - padding
    const spaceAbove = anchorRect.top - topBound - padding

    const best = Math.max(spaceBelow, spaceAbove)
    maxHeight.value = best > MIN_USEFUL_HEIGHT_PX ? Math.floor(best) : null
  }

  const refreshBoundaryObserver = (): void => {
    const el = getAnchorEl()
    const boundaries = el ? findBounds(el, selector).boundaries : []

    const sameSet =
      boundaries.length === observedBoundaries.length &&
      boundaries.every((b, i) => b === observedBoundaries[i])

    if (sameSet) {
      return
    }

    resizeObserver?.disconnect()
    observedBoundaries = boundaries

    if (boundaries.length > 0 && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(compute)
      for (const b of boundaries) {
        resizeObserver.observe(b)
      }
    } else {
      resizeObserver = null
    }
  }

  const onScroll = useDebounceFn(compute, 300, { maxWait: 1000 })
  const onResize = useDebounceFn(compute, 300, { maxWait: 1000 })

  const subscribe = (): void => {
    refreshBoundaryObserver()
    compute()
    ScrollManager.getInstance().subscribe(onScroll)
    window.addEventListener('resize', onResize)
  }

  const unsubscribe = (): void => {
    ScrollManager.getInstance().unsubscribe(onScroll)
    window.removeEventListener('resize', onResize)
    resizeObserver?.disconnect()
    resizeObserver = null
    observedBoundaries = []
    maxHeight.value = null
  }

  onBeforeUnmount(unsubscribe)

  const popupContentClass = computed<string | undefined>(() => {
    return maxHeight.value !== null ? MENU_MAX_HEIGHT_CLASS : undefined
  })

  const popupContentStyle = computed<Record<string, string> | undefined>(() => {
    return maxHeight.value !== null ? { '--ds-menu-max-height': `${maxHeight.value}px` } : undefined
  })

  return {
    subscribe,
    unsubscribe,
    popupContentClass,
    popupContentStyle,
  }
}
